import { useState, useEffect, useMemo } from 'react'
import { Shard, ShardStatus, LogEntry } from '../types/shard'
import { Worker } from './useWorkers'

export interface MetricSnapshot {
  timestamp: number
  tasksCompleted: number
  tasksInFlight: number
  tasksBlocked: number
  activeWorkers: number
  totalWorkers: number
  specsCompleted: number
  totalSpecs: number
}

export interface TimelineSegment {
  shardId: string
  shardTitle: string
  workerName: string
  status: ShardStatus
  startTime: number  // ms epoch
  endTime: number | null  // null = still active
}

export interface WorkerLifespan {
  name: string
  persona: string
  spawnTime: number
  deathTime: number | null
}

const MAX_SNAPSHOTS = 120  // 2 minutes of history at 1s intervals
const SNAPSHOT_INTERVAL_MS = 1000

// Terminal statuses where work is done
const DONE_STATUSES: ShardStatus[] = ['DONE', 'APPROVED', 'FINAL_APPROVED']

// Parse a log entry's message to extract task/activity info
function parseLogMessage(log: LogEntry): {
  persona: string
  eventType: string
  taskId: string | null
  activityType: string | null
} | null {
  // message format: "[Persona] EVENT_TYPE description..."
  const match = log.message.match(/^\[(\w+)\]\s+(\w+)\s+(.*)/)
  if (!match) return null

  const persona = match[1]!
  const eventType = match[2]!
  const description = match[3]!

  // Check for task ID in description
  const taskMatch = description.match(/((?:TASK|SPEC)-\d+)/)
  if (taskMatch) {
    return { persona, eventType, taskId: taskMatch[1]!, activityType: null }
  }

  // Non-task activities (exploration, brainstorm, discuss, etc.)
  const activityMatch = description.match(/^(exploration|brainstorm|discuss|task breakdown|orchestration|targeted mode)/)
  if (activityMatch) {
    return { persona, eventType, taskId: null, activityType: activityMatch[1]! }
  }

  return { persona, eventType, taskId: null, activityType: description.split(' ')[0] || null }
}

// Normalize a timestamp string to UTC epoch ms.
// Activity log timestamps lack "Z" suffix but ARE UTC — add it to match
// worker created_at timestamps which have "Z".
function toEpoch(ts: string): number {
  // If it already has timezone info (Z or +/-offset), parse as-is
  if (ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts)) {
    return new Date(ts).getTime()
  }
  // Otherwise treat as UTC by appending Z
  return new Date(ts + 'Z').getTime()
}

// Build timeline segments from activity log entries
function buildSegmentsFromLogs(logs: LogEntry[], shards: Shard[]): TimelineSegment[] {
  const shardMap = new Map(shards.map(s => [s.id, s]))
  const segments: TimelineSegment[] = []

  // Track open segments by "worker:taskId" key
  const openSegments = new Map<string, {
    workerName: string
    startTime: number
    shardId: string
    shardTitle: string
    persona: string
  }>()

  // Sort logs by timestamp (normalize to UTC)
  const sortedLogs = [...logs]
    .filter(l => l.source === 'activity' || l.source === 'Activity')
    .sort((a, b) => toEpoch(a.timestamp) - toEpoch(b.timestamp))

  let lastEventTime = 0

  for (const log of sortedLogs) {
    const parsed = parseLogMessage(log)
    if (!parsed) continue

    const workerName = log.worker || ''
    if (!workerName) continue

    const { eventType, taskId, activityType, persona } = parsed
    const segmentId = taskId || activityType || 'unknown'
    const key = `${workerName}:${segmentId}`
    const timestamp = toEpoch(log.timestamp)
    lastEventTime = Math.max(lastEventTime, timestamp)

    if (eventType === 'START') {
      // Close any existing open segment for this worker+activity
      const existing = openSegments.get(key)
      if (existing) {
        segments.push({
          shardId: existing.shardId,
          shardTitle: existing.shardTitle,
          workerName: existing.workerName,
          status: 'IN_PROGRESS',
          startTime: existing.startTime,
          endTime: timestamp,
        })
      }

      const shard = taskId ? shardMap.get(taskId) : null
      openSegments.set(key, {
        workerName,
        startTime: timestamp,
        shardId: segmentId,
        shardTitle: shard?.title || activityType || segmentId,
        persona,
      })
    } else if (eventType === 'DONE') {
      const open = openSegments.get(key)
      if (open) {
        // Extract final status from DONE message if available
        const statusMatch = log.message.match(/status:\s*(\w+)/)
        const finalStatus = statusMatch ? statusMatch[1] as ShardStatus : 'DONE'
        const shard = taskId ? shardMap.get(taskId) : null

        segments.push({
          shardId: open.shardId,
          shardTitle: shard?.title || open.shardTitle,
          workerName: open.workerName,
          status: finalStatus,
          startTime: open.startTime,
          endTime: timestamp,
        })
        openSegments.delete(key)
      }
    }
  }

  // Close remaining open segments at the last event time (not open-ended).
  // Leaving them as endTime: null would stretch the timeline to "now",
  // making it days wide with all data bunched on the far left.
  const closeTime = lastEventTime || Date.now()
  for (const [, open] of openSegments) {
    const shard = shardMap.get(open.shardId)
    segments.push({
      shardId: open.shardId,
      shardTitle: shard?.title || open.shardTitle,
      workerName: open.workerName,
      status: shard?.status || 'IN_PROGRESS' as ShardStatus,
      startTime: open.startTime,
      endTime: closeTime,
    })
  }

  return segments
}

export function useMetrics(shards: Shard[], workers: Worker[], logs: LogEntry[]) {
  const [snapshots, setSnapshots] = useState<MetricSnapshot[]>([])
  const [workerLifespans, setWorkerLifespans] = useState<Map<string, WorkerLifespan>>(new Map())

  // Snapshot on interval
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      const snapshot: MetricSnapshot = {
        timestamp: now,
        tasksCompleted: shards.filter(s => s.status === 'DONE').length,
        tasksInFlight: shards.filter(s => s.status === 'IN_PROGRESS').length,
        tasksBlocked: shards.filter(s => s.status === 'BLOCKED').length,
        activeWorkers: workers.filter(w => w.status === 'active').length,
        totalWorkers: workers.length,
        specsCompleted: shards.filter(s => s.id.startsWith('SPEC') && s.status === 'DONE').length,
        totalSpecs: shards.filter(s => s.id.startsWith('SPEC')).length,
      }

      setSnapshots(prev => {
        const next = [...prev, snapshot]
        return next.length > MAX_SNAPSHOTS ? next.slice(-MAX_SNAPSHOTS) : next
      })
    }, SNAPSHOT_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [shards, workers])

  // Build segments from activity log data
  const segments = useMemo(() => {
    if (logs.length === 0) return []
    return buildSegmentsFromLogs(logs, shards)
  }, [logs, shards])

  // Track worker lifespans
  useEffect(() => {
    setWorkerLifespans(prev => {
      const next = new Map(prev)
      const now = Date.now()

      for (const worker of workers) {
        if (!next.has(worker.name)) {
          next.set(worker.name, {
            name: worker.name,
            persona: worker.persona,
            spawnTime: worker.created_at ? new Date(worker.created_at).getTime() : now,
            deathTime: worker.status === 'inactive' ? now : null,
          })
        } else {
          const existing = next.get(worker.name)!
          if (worker.status === 'inactive' && existing.deathTime === null) {
            next.set(worker.name, { ...existing, deathTime: now })
          } else if (worker.status === 'active' && existing.deathTime !== null) {
            next.set(worker.name, { ...existing, deathTime: null })
          }
        }
      }

      return next
    })
  }, [workers])

  // Computed metrics
  const currentMetrics = useMemo(() => {
    const specs = shards.filter(s => s.id.startsWith('SPEC'))
    const tasks = shards.filter(s => !s.id.startsWith('SPEC'))

    const completedTasks = tasks.filter(t => DONE_STATUSES.includes(t.status))

    let avgCycleTimeMs = 0
    if (completedTasks.length > 0) {
      const withUpdated = completedTasks.filter(t => t.created_at && t.updated_at)
      if (withUpdated.length > 0) {
        avgCycleTimeMs = withUpdated.reduce((sum, t) => {
          const start = new Date(t.created_at!).getTime()
          const end = new Date(t.updated_at!).getTime()
          return sum + (end - start)
        }, 0) / withUpdated.length
      } else {
        const withCreated = completedTasks
          .filter(t => t.created_at)
          .map(t => new Date(t.created_at!).getTime())
          .sort((a, b) => a - b)

        if (withCreated.length >= 2) {
          const totalSpan = withCreated[withCreated.length - 1]! - withCreated[0]!
          avgCycleTimeMs = totalSpan / completedTasks.length
        }
      }
    }

    return {
      tasksCompleted: completedTasks.length,
      tasksInFlight: tasks.filter(t => t.status === 'IN_PROGRESS').length,
      tasksBlocked: tasks.filter(t => t.status === 'BLOCKED').length,
      activeWorkers: workers.filter(w => w.status === 'active').length,
      totalWorkers: workers.length,
      specsCompleted: specs.filter(s => DONE_STATUSES.includes(s.status)).length,
      totalSpecs: specs.length,
      avgCycleTimeMs,
    }
  }, [shards, workers])

  return {
    snapshots,
    segments,
    workerLifespans,
    currentMetrics,
  }
}
